/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Popup, Header, Checkbox } from 'semantic-ui-react';
import cn from 'classnames';

import { CacheInputs, Portfolio, Storage, Accounts, Analytics } from 'actions';
import { ACCOUNTS_SAVE_AS_PORTFOLIO, ACCOUNTS_COMPARE } from 'utils/features';
import { Feature, Button as CustomButton, Modal } from 'components';
import { colors } from 'utils/colors';
import { isGlobeInvestor, isStandalone, isQuoteMedia } from 'utils/helpers';
import SaveModal from './SaveModal';
import './SideBar.css'; // Created css stylesheet as the :before element (arrow) for the new feature popup needed it to be targeted

class SideBar extends Component {
  state = {
    modalIsOpen: false,
    reminderIsOpen: false,
  };

  componentDidMount() {
    const { ui_compare_checked } = this.props;
    if (!ui_compare_checked) {
      this.setReminderVisibility(true);
    }
  }

  componentWillUnmount() {
    const { dispatch, location, not_save_url_scorecard, history } = this.props;
    const portfolioPaths = /^\/portfolio\/.*$/;
    dispatch(Storage.removeItem('to_scorecard'));

    if (
      not_save_url_scorecard &&
      !portfolioPaths.test(history.location.pathname)
    ) {
      dispatch(CacheInputs.captureUrl('portfolio', '/portfolio/landing'));

      return dispatch(Storage.removeItem('not_save_url_scorecard'));
    }

    return dispatch(CacheInputs.captureUrl('portfolio', location.pathname));
  }

  generatePDF = () => {
    const { history, match, partnerName } = this.props;
    if (isStandalone(partnerName)) {
      return history.push('/scorecard/pdf');
    }

    return history.push(`/portfolio/${match.params.id}/pdf`);
  };

  renderCheckboxes = () => {
    const { dispatch, printParams, esg, portfolio } = this.props;
    const leftArr = [
      'Overview',
      'Historical Performance Graph',
      'Performance',
      'Downside',
      'Fees',
      'Income',
    ];
    // 'Diversification' is the printParams key for what now displays as
    // "Correlation Matrix" — it lives inside subClass since it's indented
    // under the new master "Diversification" toggle.
    const rightArr = [
      { name: 'DiversificationAll', label: 'Diversification', isMaster: true },
      { name: 'Diversification', label: 'Correlation Matrix' },
      { name: 'Asset Class' },
      { name: 'Individual' },
      { name: 'Region' },
      { name: 'Global Stock Sector' },
      { name: 'Macro Factor' },
      { name: 'Equity Style' },
      { name: 'ESG' },
    ];
    const subClass = [
      'Diversification',
      'Asset Class',
      'Individual',
      'Region',
      'Global Stock Sector',
      'Macro Factor',
      'Equity Style',
    ];
    const isSubclass = (name) => subClass.includes(name);
    const trigerAlertWithoutEsg = true;

    const checkbox = (item) => {
      const { name, label, isMaster } = item;
      const displayLabel = label || name;

      // Master toggle is "on" only when every subclass field is checked.
      const checked = isMaster
        ? subClass.every((key) => printParams[key])
        : !!printParams[name];

      const handleChange = () => {
        if (isMaster) {
          const newValue = !checked;
          const updates = subClass.reduce((acc, key) => {
            acc[key] = newValue;
            return acc;
          }, {});
          dispatch(Analytics.updatePrintParams(updates));
          return;
        }

        if (
          name === 'ESG' &&
          Object.keys(esg).length === 0 &&
          !printParams.ESG
        ) {
          dispatch(Analytics.fetchEsg(portfolio, trigerAlertWithoutEsg));
        }

        dispatch(
          Analytics.updatePrintParams({
            [name]: !printParams[name],
          })
        );
      };

      return (
        <BlackCheckboxStyle key={name}>
          <div
            className="ui checkbox"
            style={{
              display: 'block',
              margin: isSubclass(name) ? '1rem 0 0 2rem' : '1rem 0 0 0',
            }}
          >
            <input type="checkbox" checked={checked} onChange={handleChange} />
            <label
              style={{
                color: 'rgba(0,0,0,0.7)',
                fontSize: '1.14em',
                fontWeight: 'normal',
              }}
            >
              {displayLabel}
            </label>
          </div>
        </BlackCheckboxStyle>
      );
    };

    return (
      <div style={{ padding: '0.5rem 1.5rem' }}>
        <div style={{ width: '49%', float: 'left' }}>
          {(leftArr || []).map((elem) => checkbox({ name: elem }))}
        </div>
        <div style={{ width: '49%', float: 'right' }}>
          {(rightArr || []).map((elem) => checkbox(elem))}
        </div>
      </div>
    );
  };

  setReminderVisibility = (isOpen) => this.setState({ reminderIsOpen: isOpen });

  handleReminderClick = () => {
    const { dispatch } = this.props;
    dispatch(Storage.setItem('ui_compare_checked', true));
    dispatch(Accounts.checkUiCompare());
    this.setState({ reminderIsOpen: false });
  };

  isSaveVisible = (portfolio) => {
    const { match, partnerName } = this.props;

    if (
      !portfolio ||
      isGlobeInvestor(partnerName) ||
      partnerName === 'nbdb_sandbox' ||
      partnerName === 'nbdb'
    ) {
      return false;
    }

    if (match.params.id !== 'unsaved') {
      if (!portfolio.is_mp) {
        return false;
      }
    }

    return true;
  };

  redirect = (portfolio) => {
    const { id, history, dispatch } = this.props;

    if (portfolio.name) {
      const port_id = portfolio.is_mp ? '' : id;
      dispatch(
        Portfolio.setEditing(port_id, portfolio.name, portfolio.description)
      );
    }

    if (portfolio.portfolio_type === 'quantified') {
      return history.push(`/portfolio/build-model/${id}`);
    }

    if (
      portfolio.portfolio_type === 'custom' ||
      portfolio.portfolio_type === 'unknown'
    ) {
      if (portfolio.portfolio_sub_type === 'ml_asset_allocation')
        return history.push('/portfolio/build-ml/new');

      return history.push(`/portfolio/build-custom/${id}`);
    }

    return history.push(`/portfolio/build-etf/${id}/assets`);
  };

  closeModal = () => this.setState({ modalIsOpen: false });

  handleClick = (to, name) => {
    const { history } = this.props;

    if (to) {
      return history.push(to);
    }

    return this.setState({ modalIsOpen: name });
  };

  handleCompareClick = () => {
    const { history, id, portfolio, dispatch } = this.props;
    if (portfolio.name) {
      const port_id = portfolio.is_mp ? '' : id;
      dispatch(
        Portfolio.setEditing(port_id, portfolio.name, portfolio.description)
      );
    }
    const portfolioRegex = /^\/portfolio\/.*\/compare$/;
    this.handleReminderClick();
    if (!portfolioRegex.test(history.location.pathname)) {
      return this.handleClick(`/portfolio/${id}/compare`);
    }
    return this.handleClick(`/portfolio/${id}/overview`);
  };

  moreThanOneGic = () => {
    const { portfolio } = this.props;

    return portfolio.gic_info && Object.keys(portfolio.gic_info).length > 1;
  };

  hasTenGic = () => {
    const { portfolio } = this.props;

    return portfolio.gic_info && Object.keys(portfolio.gic_info).length === 10;
  };

  render() {
    const {
      portfolio,
      hidden,
      to_scorecard,
      french,
      partnerName,
      dispatch,
      printParams,
      initHasExcludedHolding,
      history,
    } = this.props;
    const { modalIsOpen, reminderIsOpen } = this.state;
    const { printModal, printLetterGrade } = printParams;
    const isCompare = history.location.pathname.includes('/compare');

    return (
      <div>
        <Container hidden={hidden}>
          <ul>
            <Feature name={ACCOUNTS_SAVE_AS_PORTFOLIO}>
              {this.isSaveVisible(portfolio) && !this.moreThanOneGic() && (
                <Popup
                  style={{ zIndex: 3 }}
                  trigger={
                    <li
                      onClick={() => this.handleClick(false, 'save')}
                      className={cn({ active: modalIsOpen === 'save' })}
                    >
                      <button>
                        <i className="ui icon save outline" />
                        <span className="nav-label">Save</span>
                      </button>
                    </li>
                  }
                  position="right center"
                  content={
                    initHasExcludedHolding
                      ? french
                        ? 'Si vous réglez les avoirs à court historique sur NON, ils seront exclus des comparaisons et des simulations de Planification. Confirmez votre choix avant d’enregistrer.'
                        : 'If short-history holdings are toggled to NO, they will be excluded from comparisons and Planning simulations. Confirm your choice before saving.'
                      : french
                      ? 'Enregistrez ce portefeuille pour l’utiliser dans comparer, planification et outils.'
                      : 'Save this portfolio for use in Compare, Planning and Tools.'
                  }
                  basic
                  wide
                />
              )}
            </Feature>
            {portfolio &&
            (portfolio.portfolio_type === null ||
              portfolio.portfolio_type === undefined ||
              portfolio.is_mp) ? null : (
              <Popup
                style={{ zIndex: 3 }}
                trigger={
                  <li onClick={() => this.redirect(portfolio)}>
                    <button>
                      <i className="ui icon pencil" />
                      <span className="nav-label">Edit</span>
                    </button>
                  </li>
                }
                position="right center"
                content={
                  french ? 'Modifier ce portefeuille' : 'Edit this Portfolio'
                }
                basic
              />
            )}

            <Feature name={ACCOUNTS_COMPARE}>
              {!this.hasTenGic() && !reminderIsOpen && (
                <Popup
                  style={{
                    zIndex: 3,
                    left: '55px',
                    width:
                      window.location.pathname.split('/').indexOf('compare') !==
                      -1
                        ? '160px'
                        : to_scorecard
                        ? '220px'
                        : '150px',
                  }}
                  trigger={
                    <li
                      onClick={this.handleCompareClick}
                      className={cn({
                        active:
                          window.location.pathname
                            .split('/')
                            .indexOf('compare') !== -1,
                      })}
                    >
                      <button
                        type="button"
                        aria-label={
                          french ? 'Comparer / Et si' : 'Compare / What-If'
                        }
                      >
                        <i className="ui icon law" />
                        <span className="nav-label">Compare</span>
                      </button>
                    </li>
                  }
                  position="right center"
                  content={
                    window.location.pathname.split('/').indexOf('compare') !==
                    -1
                      ? french
                        ? 'Retour à la fiche de pointage'
                        : 'Return to Scorecard'
                      : to_scorecard
                      ? french
                        ? 'Retour à Comparer / Et si'
                        : 'Return to Compare / What-if'
                      : french
                      ? 'Comparer / Et si'
                      : 'Compare / What-If'
                  }
                  basic
                />
              )}
              <Popup
                style={{ zIndex: 3 }}
                trigger={
                  <li
                    className={cn({ active: printModal, disabled: isCompare })}
                    onClick={() => {
                      if (!isCompare) {
                        if (isQuoteMedia(partnerName)) {
                          this.setState({ modalIsOpen: 'download' });
                          dispatch(
                            Analytics.updatePrintParams({
                              printModal: true,
                            })
                          );
                        } else {
                          this.generatePDF();
                        }
                      }
                    }}
                  >
                    <button
                      type="button"
                      aria-label={
                        french
                          ? 'Imprimer la fiche de pointage'
                          : 'Print scorecard'
                      }
                    >
                      <i className="ui icon print" />
                      <span className="nav-label">
                        Print/
                        <br />
                        PDF
                      </span>
                    </button>
                  </li>
                }
                position="right center"
                content={
                  isCompare
                    ? french
                      ? 'Ce bouton sert à afficher la fiche d’évaluation du portefeuille. Pour imprimer la comparaison des portefeuilles, veuillez utiliser le bouton « Imprimer » au bas de la page.'
                      : 'This button is for the Portfolio Scorecard. To print the Portfolio Comparison, please use the “Print” button at the bottom.'
                    : french
                    ? "Imprimez ou enregistrez cette fiche d'évaluation au format PDF"
                    : 'Print or save this scorecard as a PDF'
                }
                basic
                wide
              />
              {!this.hasTenGic() && reminderIsOpen && (
                <Popup
                  className="newPopup"
                  onClick={this.handleReminderClick}
                  trigger={
                    <li
                      onClick={this.handleCompareClick}
                      className={cn({
                        active:
                          window.location.pathname
                            .split('/')
                            .indexOf('compare') !== -1,
                      })}
                    >
                      <button>
                        <i className="ui icon law" />
                        <span className="nav-label">Compare</span>
                      </button>
                    </li>
                  }
                  position="bottom center"
                  open={reminderIsOpen}
                >
                  <Popup.Header>
                    {french ? 'Regardez ça !' : 'Check this out!'}
                  </Popup.Header>
                  <Popup.Content>
                    <br />
                    {french
                      ? 'Cliquez sur cette icône d\'échelle pour essayer notre fonction "Comparer/Et si".'
                      : 'Click this scale icon to try our “Compare/What-If” function.'}
                    <br />
                    <br />
                    <span
                      style={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      {french ? 'Compris.' : 'Got it.'}
                    </span>
                  </Popup.Content>
                </Popup>
              )}
            </Feature>
          </ul>
        </Container>
        <SaveModal
          portfolio={portfolio}
          closeModal={this.closeModal}
          open={modalIsOpen === 'save'}
        />
        <Modal
          dimmerClick={() => {
            dispatch(
              Analytics.updatePrintParams({
                printModal: false,
              })
            );
          }}
          basic
          open={printModal}
        >
          <Modal.Header>
            <CloseIcon
              onClick={() => {
                dispatch(
                  Analytics.updatePrintParams({
                    printModal: false,
                  })
                );
              }}
            >
              +
            </CloseIcon>

            <Header style={{ color: 'black', width: '95%' }}>
              Would you like to include the letter grades and percentage scores
              in the PDF of your scorecard?
            </Header>
            <div
              style={{
                color: 'grey',
                fontWeight: 'normal',
                fontSize: '1.14rem',
              }}
            >
              Note: To save the scorecard to your computer, please select "Save
              as PDF" as your Print destination.
            </div>
          </Modal.Header>
          <Modal.Actions
            style={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <p style={{ paddingRight: '10px' }}>
              Include letter grades and percentage scores
            </p>
            <GreenToggleStyle>
              <Checkbox
                toggle
                checked={printLetterGrade === true}
                onChange={(e, { checked }) =>
                  dispatch(
                    Analytics.updatePrintParams({
                      printLetterGrade: checked,
                    })
                  )
                }
                style={{
                  fontSize: '1.2em',
                  color: 'black',
                  fontWeight: 'normal',
                }}
              />
            </GreenToggleStyle>
          </Modal.Actions>
          {printLetterGrade !== null ? (
            <div>
              <Modal.Header style={{ padding: '1rem 1.5rem 0rem' }}>
                <Header style={{ color: 'black' }}>
                  Which pages would you like to include?
                </Header>
              </Modal.Header>
              {this.renderCheckboxes()}
              <div
                style={{
                  clear: 'both',
                  paddingTop: '2rem',
                  marginBottom: '2rem',
                  textAlign: 'center',
                }}
              >
                <CustomButton
                  className="left"
                  color={colors.green}
                  icon="checkmark"
                  onClick={() => {
                    dispatch(
                      Analytics.updatePrintParams({
                        printModal: false,
                      })
                    );
                    this.generatePDF();
                  }}
                >
                  Done
                </CustomButton>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    );
  }
}

SideBar.propTypes = {
  location: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  portfolio: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  hidden: PropTypes.bool,
  not_save_url_scorecard: PropTypes.bool,
  ui_compare_checked: PropTypes.bool,
  to_scorecard: PropTypes.bool,
  partnerName: PropTypes.string,
  french: PropTypes.bool,
  printParams: PropTypes.object,
  esg: PropTypes.object,
  initHasExcludedHolding: PropTypes.bool,
};

SideBar.defaultProps = {
  hidden: false,
  not_save_url_scorecard: false,
  ui_compare_checked: false,
  to_scorecard: false,
  partnerName: '',
  french: false,
  printParams: {},
  esg: {},
  initHasExcludedHolding: false,
};

export default withRouter(
  connect((state) => ({
    editing: state.Portfolio.editing,
    not_save_url_scorecard: state.Storage.not_save_url_scorecard,
    ui_compare_checked: state.Storage.ui_compare_checked,
    to_scorecard: state.Storage.to_scorecard,
    partnerName: state.Storage['partner-name'],
    french: state.Storage.language === 'fr',
    printParams: state.Analytics.printParams,
    esg: state.Analytics.esg,
  }))(SideBar)
);

const Container = styled.div`
  display: ${(props) => (props.hidden ? 'none' : 'block')};
  position: fixed;
  top: 60px;
  left: 0;
  width: 4.8rem;
  height: 100%;
  background: ${colors.darkBlue};
  padding: 0;
  z-index: 2;

  ul {
    margin: 0;
    padding: 0.4rem 0;
    list-style: none;

    li {
      color: white;
      text-align: center;
      width: 3.4rem;
      margin: 0.2rem auto;
      padding: 0.6rem 0;
      font-size: 1.5rem;
      border-radius: 10px;
      transition: all 200ms ease;
      cursor: pointer;
      background: transparent;

      &.active {
        color: white;
        background: rgba(255, 255, 255, 0.15);
      }

      &.disabled {
        opacity: 0.5;
        color: white;
        cursor: default;
      }

      &:hover {
        color: white;
        background: rgba(255, 255, 255, 0.12);
      }

      button {
        background: transparent;
        border: none;
        outline: none;
        padding: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        color: white;
        cursor: inherit;

        .nav-label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          margin-top: 0.3rem;
          line-height: 1.2;
          letter-spacing: 0.02em;
          color: white;
          font-family: inherit;
        }

        svg {
          font-size: 1.5rem;
        }
      }
    }
  }

  i {
    margin: 0;
    width: fit-content;
    height: fit-content;
    color: white !important;
    font-size: 1.5rem;

    &.save {
      padding: 2px 4px;
    }

    &.pencil {
      padding: 2px 4px;
    }

    &.undo {
      padding: 2px 4px;
    }

    &.money {
      padding: 2px 1px;
    }

    &.law {
      font-size: 1.3rem;
      padding: 1px 0;
    }

    &.print {
      padding: 2px 4px;
    }
  }
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  height: 20px;
  width: 20px;
  text-align: center;
  font-size: 1.7rem;
  border-radius: 50%;
  line-height: 19px;
  transform: rotate(45deg);
  transition: all 0.1s ease;
  cursor: pointer;
  &:hover {
    background: rgba(0, 0, 0, 0.8);
    color: #fff6f6;
  }
`;

const GreenToggleStyle = styled.div`
  .ui.toggle.checkbox input[type='checkbox']:checked ~ label::before,
  .ui.toggle.checkbox input[type='checkbox']:checked:focus ~ label::before {
    background-color: ${colors.green} !important; /* Your desired color */
  }
`;

const BlackCheckboxStyle = styled.div`
  .ui.checkbox input[type='checkbox']:checked ~ label:after {
    color: rgba(0, 0, 0, 0.7) !important;
    border-color: rgba(0, 0, 0, 0.7) !important;
  }
`;
